import { useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { AlertBanner } from '../components/AlertBanner.jsx';
import { ConfirmDialog } from '../components/ConfirmDialog.jsx';
import { TONES } from '../constants.jsx';
import { mockSave } from '../services/botConfigService.js';

export function TonePanel({ bot, updateBot, mockErrorOn, notify }) {
  const [selected, setSelected] = useState(bot.tone);
  const [status, setStatus] = useState('idle'); // idle | saving | error
  const [error, setError] = useState(null);
  const [pendingTone, setPendingTone] = useState(null);

  function attemptSelect(id) {
    if (status === 'saving') return;
    if (bot.status === 'published' && bot.tone && bot.tone !== id) {
      setPendingTone(id);
      return;
    }
    setError(null);
    setSelected(id);
  }

  // Checklist item: "Selection is keyboard-navigable with arrow keys"
  function onCardKeyDown(e, currentId) {
    const idx = TONES.findIndex((t) => t.id === currentId);
    if (idx === -1) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = TONES[(idx + 1) % TONES.length];
      attemptSelect(next.id);
      document.getElementById(`tone-card-${next.id}`)?.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = TONES[(idx - 1 + TONES.length) % TONES.length];
      attemptSelect(prev.id);
      document.getElementById(`tone-card-${prev.id}`)?.focus();
    }
  }

  function save() {
    if (!selected) {
      setError('Choose a tone to continue');
      return;
    }
    setError(null);
    setStatus('saving');
    mockSave(mockErrorOn)
      .then(() => {
        updateBot({ tone: selected });
        setStatus('idle');
        notify('success', 'Tone updated');
      })
      .catch(() => setStatus('error'));
  }

  return (
    <div>
      <div className="panel-header">
        <h1>Tone</h1>
        <p>Choose the voice your bot uses in every reply. You can change this any time.</p>
      </div>

      {status === 'error' && (
        <AlertBanner text="We couldn't save your changes. Try again." onRetry={save} />
      )}

      <div className="card">
        {/* Bootstrap grid: 1 col on mobile, 2 on tablet+, replacing the old
            hand-rolled CSS grid. .tone-card keeps its ChatBizz styling
            (border, selected state, hover) from index.css. */}
        <div
          className={'row row-cols-1 row-cols-md-2 g-3' + (error ? ' tone-group-invalid' : '')}
          role="radiogroup"
          aria-label="Bot tone"
          aria-invalid={!!error}
        >
          {TONES.map((t) => (
            <div className="col" key={t.id}>
              <button
                id={`tone-card-${t.id}`}
                type="button"
                role="radio"
                aria-checked={selected === t.id}
                tabIndex={selected === t.id || (!selected && t.id === TONES[0].id) ? 0 : -1}
                disabled={status === 'saving'}
                className={
                  'tone-card w-100 h-100 text-start position-relative' +
                  (selected === t.id ? ' selected' : '')
                }
                onClick={() => attemptSelect(t.id)}
                onKeyDown={(e) => onCardKeyDown(e, t.id)}
              >
                {status === 'saving' && selected === t.id && (
                  <span className="tone-card-saving-overlay d-flex align-items-center justify-content-center">
                    <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>
                  </span>
                )}
                <div className="tone-name">
                  <span>{t.name}</span>
                  {selected === t.id && (
                    <span className="badge rounded-pill text-bg-primary check-circle">
                      <Icon.check s={10} />
                    </span>
                  )}
                </div>
                <div className="tone-example">&ldquo;{t.example}&rdquo;</div>
              </button>
            </div>
          ))}
        </div>

        {error && (
          <div className="error-text mt-2" role="alert" id="tone-error">
            <Icon.alert s={12} /> {error}
          </div>
        )}

        <div className="actions-row">
          <button className="btn-primary" disabled={status === 'saving'} onClick={save} aria-describedby={error ? 'tone-error' : undefined}>
            {status === 'saving' && (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            )}
            {status === 'saving' ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {pendingTone && (
        <ConfirmDialog
          title="This bot is live"
          body="Visitors will see the new tone immediately. Continue?"
          confirmLabel="Change tone"
          onConfirm={() => {
            setSelected(pendingTone);
            setPendingTone(null);
          }}
          onCancel={() => setPendingTone(null)}
        />
      )}
    </div>
  );
}
